/**
 * The probe matrix's shape and outcome handling: one row per access probe the backend exposes,
 * each run individually caught — a 401/403 row is enforcement *working*, so denials are data
 * to display, not exceptions to escape. Components stay thin around this.
 */
import {
  ApiError,
  probeAnonymous,
  probeAuthenticated,
  probeGlobalAdmin,
  probeTenantMember,
  probeTenantOwner,
} from '../api/client';
import type { ProbeResponse } from '../api/types';

export interface ProbeDefinition {
  readonly id: string;
  readonly label: string;
  /** What the backend requires for a 200 — shown so a denial explains itself. */
  readonly requires: string;
  readonly run: () => Promise<ProbeResponse>;
}

export interface ProbeOutcome {
  /** The HTTP status (0 when the request never got a response). */
  readonly status: number;
  /** The domain error code on a denial, null on success or a network failure. */
  readonly code: string | null;
  readonly allowed: boolean;
  readonly detail: string;
}

/** The matrix rows: the three global probes plus member/owner against the chosen test tenant. */
export function probeDefinitions(tenant: string): readonly ProbeDefinition[] {
  return [
    { id: 'anonymous', label: 'Anonymous', requires: 'nothing — always 200', run: () => probeAnonymous() },
    { id: 'authenticated', label: 'Authenticated', requires: 'any validated platform token', run: () => probeAuthenticated() },
    { id: 'global-admin', label: 'Global admin', requires: 'a grant on the internal tenant', run: () => probeGlobalAdmin() },
    { id: 'member', label: `Member of ${tenant}`, requires: `any grant on ${tenant}`, run: () => probeTenantMember(tenant) },
    { id: 'owner', label: `Owner of ${tenant}`, requires: `an Owner grant on ${tenant}`, run: () => probeTenantOwner(tenant) },
  ];
}

/** Runs one probe, folding allowed / denied / unreachable into a displayable outcome. */
export async function runProbe(definition: ProbeDefinition): Promise<ProbeOutcome> {
  try {
    const response = await definition.run();
    return {
      status: 200,
      code: null,
      allowed: true,
      detail: response.email === null ? response.requirement : `as ${response.email}`,
    };
  } catch (e: unknown) {
    if (e instanceof ApiError) {
      return { status: e.status, code: e.body.code, allowed: false, detail: e.body.description };
    }
    return {
      status: 0,
      code: null,
      allowed: false,
      detail: e instanceof Error ? e.message : String(e),
    };
  }
}
